// Report templates: each is a written prompt the user can run in-app (their API key) or copy into their own
// ChatGPT / Claude / Gemini. Prompt text follows design/MarketReport.md; the structure headings are contractual
// (the model must reproduce them), so keep them in sync with that document.
import { t } from '@/i18n';
import { usSessionPhase } from '@/market/marketHours';
import type { Asset, Locale, ReportTemplateId } from '@/types';

export interface PromptInput {
  context: string; // holdings context block from buildHoldingsContext
  assets: Asset[]; // decides which conditional sections (crypto, non-USD cash) the prompt carries
  now: number;
  locale: Locale;
  maxSearches: number;
  scenario?: string;
}

export interface ReportTemplate {
  id: ReportTemplateId;
  needsWeb: boolean;
  /** One report per New York day (regenerating replaces it); false keeps every run. */
  daily: boolean;
  /** Whether the template makes sense right now (post-market only after the close). */
  available(now: number): boolean;
  estimateSearches(assets: Asset[]): number;
  system(input: PromptInput): string;
  user(input: PromptInput): string;
}

export const MAX_SEARCHES = 20;

const GENERAL_RULES = `你是为一位个人投资者服务的市场简报撰写助手。你会收到：这位投资者的持仓上下文（含每项资产的当前价格）、当前的纽约时间、要求的输出语言、日报类型、搜索次数上限。

【通用规则】

0. 时间与身份
- 以用户消息中给出的「当前纽约时间」为唯一的"现在"。所有"今天 / 过去 24 小时 / 最新"都相对它计算，不要使用你自己对日期的猜测。
- 判断美股当日状态：常规交易日、周末、或美国交易所假日。依据是给定日期与你搜索到的交易所日历；不确定时搜索 \`NYSE holiday calendar {YEAR}\`。

1. 数据来源（最高优先级）
- 简报中的每一条事实（行情、新闻、数据读数、日期、概率）只能来自两处：(a) 用户消息里的持仓上下文；(b) 你在本次任务中通过搜索工具获取到的、带 URL 的网页内容。
- 禁止把训练记忆中的任何数字、事件或"常识性判断"当作事实来源。训练数据滞后，可能不认识近期上市的公司、新代码、改名或最新事件。例：SPCX 是 SpaceX 上市后的代码，不能因为你不认识就当作不存在或未上市。持仓上下文中的代码与名称一律视为真实存在，并以它们为准去搜索。
- 持仓的价格一律取自持仓上下文（它来自用户自己的行情源，每项都带报价时间）：现价、开盘、最低–最高、昨收、今日涨跌。大盘的当日涨跌同样取自上下文末尾的「市场基准」一段（有的是指数本身，有的以跟踪 ETF 的当日涨跌代理，以该段的标注为准，代理项写明「ETF 代理」）。不要为了查持仓或指数的价格去搜索——搜索结果里很少有精确的当日开收盘价；上下文没有给出的价格项直接略去，不要写「未获取到」。新闻里顺带出现的价格与上下文不一致时以上下文为准，并用一句话注明差异与该来源的时间。
- 查不到就说查不到：某项需要搜索的内容（新闻、公告、宏观数据）没有结果时，写「实时查询未获取到」，然后继续下一项。不要用推测填补，也不要省略该小节。
- 每条来自搜索的事实后面必须附来源：\`— [来源标题](URL)，发布日期\`。没有 URL 的信息不写。取自持仓上下文的价格、涨跌与占比不需要附来源。

2. 搜索纪律
- 先列计划再搜索：根据持仓上下文列出需要搜索的主题清单（每项资产一条、宏观三条；结构里出现「数字货币」「非美元货币」小节时各追加相应条目），然后按清单逐一执行，不重复，不遗漏。清单本身不要输出。
- 每项资产至少搜索一次 \`{代码} {公司名} news\`（英文），必要时补搜财报、公告、评级。总搜索次数不超过用户消息给出的上限；接近上限时的优先级：先「每项资产至少一次」，再「宏观三条」，最后才是补指数点位。
- 来源优先级：交易所与公司公告、监管文件 > 主流财经媒体（Reuters、Bloomberg、WSJ、CNBC、FT、AP）> 数据发布机构官网（BLS、BEA、Federal Reserve、CME FedWatch）> 其他。避免论坛、内容农场和无日期的页面。
- 核对日期：只采用发布时间在要求窗口内的内容（"过去 24 小时"以当前纽约时间倒推）。窗口内没有的，可采用最近一次已发布的内容，但必须标注「最近一次：{日期}」。

3. 写作规则
- 用指定语言撰写；代码、公司名、机构名保留英文原文。
- 每条要点 = 一句结论 + 一句"对这位投资者的意义"（结合其持仓占比与今日涨跌）。不写泛泛的市场评论。
- 不给"必须买 / 卖"的指令；可以指出触发条件、时间点、需要盯的数据。
- 篇幅：每项资产的小节不超过 120 字，全文不超过 1,500 字（中文）或 900 词（英文）。要点数量按各节要求，宁少勿滥。
- 篇幅与搜索上限都不是省略的理由：用户消息「必须逐项覆盖」一行列出的每一项持仓（含数字货币与非美元现金）都要有自己的小节；持仓较多时把每节压到 1–2 条，而不是删掉某一项。
- 小节标题必须与模板完全一致，便于程序解析与用户阅读。模板里给出的每一节都适用于这份持仓，必须全部输出。
- 最后一行固定为：「以上为信息整理，不构成投资建议。」

4. 无搜索工具时（离线模式）
- 如果本次请求里没有可用的网络搜索工具，不要假装搜索过。首行注明「离线模式：本次未联网，仅基于持仓上下文」，只输出「持仓速览」一节（各资产现价、今日涨跌、占比、相对成本，逐项一行），跳过所有需要外部信息的小节，最后一行仍为免责声明。`;

const NUMERALS = ['一', '二', '三', '四', '五', '六', '七'];
const STABLECOINS = new Set(['USDC', 'USDT', 'DAI', 'PYUSD', 'FDUSD', 'USDE']);

/** What the portfolio holds beyond US-dollar securities; the app knows this, so the prompt never asks the model to work it out. */
export interface Held {
  crypto: string[];
  stablecoins: string[];
  fiat: string[]; // non-USD cash currencies
  canada: boolean; // any Canadian-listed stock, ETF, CDR or FundSERV fund
}

export function heldBy(assets: Asset[]): Held {
  const uniq = (xs: string[]) => Array.from(new Set(xs.map((x) => x.toUpperCase())));
  const crypto = uniq(assets.filter((a) => a.assetClass === 'crypto').map((a) => a.symbol));
  return {
    crypto,
    stablecoins: crypto.filter((c) => STABLECOINS.has(c)),
    fiat: uniq(assets.filter((a) => a.assetClass === 'cash').map((a) => a.currency)).filter((c) => c !== 'USD'),
    canada: assets.some((a) => a.market === 'CA' && a.assetClass !== 'cash'),
  };
}

/** Numbers the sections that apply, so a portfolio without crypto gets a prompt with no crypto section at all. */
function structure(sections: ({ title: string; body: string } | false)[]): string {
  return sections
    .filter((x): x is { title: string; body: string } => !!x)
    .map((x, i) => `## ${NUMERALS[i]}、${x.title}\n${x.body}`)
    .join('\n\n');
}

const stablecoinRule = (h: Held) => (h.stablecoins.length ? `\n- 稳定币（${h.stablecoins.join('、')}）按现金等价物看待：只写脱锚、发行方储备与监管方面的消息，不写价格走势。` : '');

function premarket(h: Held): string {
  const sections = structure([
    {
      title: '持仓相关新闻',
      body: [
        '按持仓上下文的顺序，每项资产一个小节，标题格式 `#### {代码} · {名称}`。',
        '- 每节 2–4 条过去约 24 小时的要点：新闻、公告、分析师评级变动、财报或业绩指引、重大事件；每条注明潜在影响与来源。',
        '- 盘前有明显异动（±3% 以上）时单列一条说明幅度与原因。',
        '- 该资产 24 小时内无重要新闻：只写一条「过去 24 小时无重要新闻（已搜索）」。',
        h.crypto.length > 0 && '- 数字货币各占一个小节（不得省略），写该币种自身的消息与过去 24 小时的涨跌；行业层面的消息归「数字货币」一节。',
        h.fiat.length > 0 && '- 法币现金各占一个小节，只写直接影响该币种的头条；宏观细节归「非美元货币」一节。',
      ].filter(Boolean).join('\n'),
    },
    {
      title: '美国宏观（通胀 / 就业 / 美联储）',
      body: `三个固定小节，每小节 2–3 条要点，各附"对利率路径与本组合的意义"。
- **通胀**：最新一期 CPI（总体与核心，同比与环比），可得时含 PCE；与上期、与市场预期的对比；发布日期；下一次发布日期。
- **就业**：最新非农新增与失业率，可得时含时薪增速与初请失业金；对比与日期；下一次发布日期。
- **美联储**：当前联邦基金利率目标区间；下次 FOMC 日期；过去 24 小时的官员讲话（尤其现任主席）、会议纪要、政策信号；CME FedWatch 对下次会议加息 / 降息 / 维持的概率（注明查询时间）。`,
    },
    h.crypto.length > 0 && {
      title: '数字货币',
      body: `本组合持有数字货币（见用户消息「数字货币持仓」一行），本节必须输出。数字货币 7×24 交易，周末与美股休市日照常写。
- 2–3 条影响整体加密市场的要点，优先监管与法律、ETF 资金流向、重大安全事件。
- 1 条针对本组合中具体币种的提醒，结合其占比与今日涨跌。${stablecoinRule(h)}`,
    },
    h.fiat.length > 0 && {
      title: '非美元货币',
      body: `每种货币一个小节 \`#### {货币代码}\`：
- 2–3 条该央行与经济体在通胀、就业、利率方面的最新消息。
- 1 条对汇率的观察，对照上下文给出的汇率。`,
    },
    { title: '今日关注', body: `3–5 条清单：今天有哪些数据发布、财报、事件会影响这份持仓，各附时间（ET）与来源。${h.crypto.length > 0 ? '持仓含数字货币：查到加密相关的日程（监管听证、ETF 审批、网络升级）时列入。' : ''}` },
  ]);
  return `【前瞻日报（盘前）结构】

首行：\`{日期（含星期）} · 盘前 · 美股今日：{正常交易 | 休市：原因}\`
第二行：一句话概括隔夜到盘前的整体基调：先用持仓上下文末尾「市场基准」的涨跌说明上一交易日大盘的收法（它的报价时间就是上一收盘），再加上如查到的美股期货与主要海外指数方向${h.crypto.length > 0 ? '，以及加密市场过去 24 小时的方向' : ''}，附来源）。

${sections}`;
}

function postmarket(h: Held): string {
  const hasCrypto = h.crypto.length > 0;
  const sections = structure([
    {
      title: '大盘',
      body: `- 道琼斯、标普 500、纳斯达克综合${h.canada ? '、S&P/TSX 综合指数' : ''}各自的当日涨跌幅，一律取自持仓上下文末尾的「市场基准」：直接写那里的百分比与报价时间，美股三项标注「ETF 代理」${h.canada ? '，S&P/TSX 还有点位、开盘与区间可写' : ''}。这一项不需要搜索，也永远不写「未获取到」；上下文里没有「市场基准」一段时才写「实时查询未获取到」。报价时间早于 16:00 ET 时注明「截至 {时间}，非最终收盘」。
- 一次搜索补充点位与走势：搜 \`stock market today close Dow S&P 500 Nasdaq {日期}\`，搜到收盘综述就把三大指数的收盘点位加在各自的百分比后面（附来源），并用一句话概括开盘、盘中、收盘的走势；点位与「市场基准」的百分比不一致时以「市场基准」为准。没搜到就只写百分比，不再重试。
- 2–3 条推动当日大盘的主要因素（宏观数据、政策、板块轮动）。`,
    },
    {
      title: '相对大盘',
      body: [
        '一张 Markdown 表格，列为：资产 | 今日涨跌 | 基准 | 基准涨跌 | 相对（百分点） | 结论。持仓上下文里除现金外的每一项都要有一行；按「相对」降序。',
        '- 今日涨跌取自持仓上下文；基准涨跌取自上下文末尾的「市场基准」（美股三项为 ETF 代理），不另行搜索。',
        `- 基准：美股与美国 ETF 用标普 500${h.canada ? '；加股、加拿大 ETF、CDR 与加拿大共同基金用 S&P/TSX 综合指数' : ''}${h.crypto.length > 0 ? '；数字货币用 BTC 的 24 小时涨跌（BTC 本身写「—」）' : ''}。`,
        '- 结论：相对 ≥ +0.1 个百分点为「跑赢」，≤ −0.1 为「跑输」，之间为「持平」。共同基金按上下文的净值日期与当日基准比较，并注明净值日期。',
        '- 表后一句话：跑赢 N 项、跑输 M 项，以及按占比加权的组合相对表现（百分点）。',
      ].join('\n'),
    },
    {
      title: '持仓逐项',
      body: [
        '按持仓上下文顺序，每项资产一个小节 `#### {代码} · {名称}`：',
        '- 当日开盘价、收盘价、最低–最高与涨跌幅：全部取自持仓上下文，不为价格搜索。收盘价就是上下文的「现价」，写明它的报价时间；报价时间晚于 16:00 ET 时注明可能含盘后交易。',
        '- 当日相关新闻或事件 1–3 条（分红、财报、信息披露、重大公告等）。',
        '- 一句话归因：大盘影响、板块因素、公司事件、或其他。',
        hasCrypto && '- 数字货币各占一个小节（不得省略）：没有开盘、收盘价，写过去 24 小时的涨跌、区间与主要驱动。',
        h.fiat.length > 0 && '- 法币现金写当日汇率变动与驱动。',
      ].filter(Boolean).join('\n'),
    },
    hasCrypto && {
      title: '数字货币',
      body: `本组合持有数字货币（见用户消息「数字货币持仓」一行），本节必须输出。
- 1–2 条当日整体加密市场的要点：BTC 与 ETH 的 24 小时方向、监管与法律、ETF 资金流向、重大安全事件。
- 1 条它与当日美股风险偏好是否同向，以及对本组合的意义。${stablecoinRule(h)}`,
    },
    { title: '下一交易日关注', body: `3–5 条清单，各附时间（ET）与来源。${hasCrypto ? '持仓含数字货币：查到加密相关的日程时列入。' : ''}` },
  ]);
  return `【收盘日报（盘后）结构】

首行：\`{日期（含星期）} · 收盘 · 美股今日：{正常交易 | 休市：原因}\`
若今日休市：「大盘」改为当天发生的、影响大盘与本持仓的新闻（5–8 条），「持仓逐项」只写当日有交易或有消息的资产${hasCrypto ? '（数字货币 7×24 交易，休市日照常写）' : ''}，其余各节照常输出。

${sections}`;
}

const checkup = (h: Held) => `你是为一位个人投资者服务的组合分析助手。本任务不联网：只依据用户消息中的持仓上下文作答，不引用训练记忆中的任何行情、新闻或估值数字；凡是需要外部数据才能下的判断，写成「需要核实：……」。

【结构】（Markdown，标题必须与下面完全一致）

## 集中度
## 相关性
## 现金与流动性
## 缺什么
## 建议的仓位区间

【写作规则】
- 每节 2–3 条要点；每条 = 一句结论 + 一句"对这位投资者的意义"，引用上下文里的占比、今日涨跌与相对成本数字。
- 「建议的仓位区间」逐项给区间百分比（例如 TSLA 10%–15%），并说明调整的先后顺序；不给"必须买 / 卖"的指令。${h.crypto.length > 0 ? `
- 本组合持有数字货币（见用户消息「数字货币持仓」一行），必须纳入分析，不能只谈股票与现金：
  - 「集中度」把数字货币作为独立大类给出占比，并指出单一币种的集中程度。
  - 「相关性」说明它在风险偏好下行时与组合内成长股、高 beta 资产同向波动的倾向；具体相关系数写成「需要核实」。
  - 「现金与流动性」数字货币 7×24 交易、波动大，不计入现金缓冲${h.stablecoins.length ? `；稳定币（${h.stablecoins.join('、')}）按现金等价物看待，同时注明发行方与脱锚风险` : ''}。
  - 「建议的仓位区间」给出每个币种各自的区间。` : ''}
- 用指定语言撰写；代码、公司名保留英文。全文不超过 900 字（中文）或 600 词（英文）。
- 最后一行固定为：「以上为信息整理，不构成投资建议。」`;

const stress = (h: Held) => `你是为一位个人投资者服务的组合分析助手。本任务不联网：只依据用户消息中的持仓上下文与给定情景作答，不引用训练记忆中的任何行情或新闻；情景里没有给出的假设，由你明确列出并标注「假设」。

【结构】（Markdown，标题必须与下面完全一致）

## 情景
用两三句话复述情景，并列出你补充的假设（各资产在该情景下的假设变动幅度）。

## 对各项持仓的影响
Markdown 表格，列为：资产 | 占比 | 假设变动 | 对总资产的影响（百分点）。按占比降序。持仓上下文里的每一项都要有一行。

## 对总资产的影响
合计影响的区间估计（乐观 / 基准 / 悲观），以及现金比例在该情景下的缓冲作用。

## 应对顺序
3–5 条：先做什么、后做什么、什么情况下不动；每条给触发条件，不给"必须"指令。

## 需要核实的假设
列出上面依赖的、需要外部数据才能确认的假设。

【写作规则】${h.crypto.length > 0 ? `
- 本组合持有数字货币（见用户消息「数字货币持仓」一行），必须纳入情景：表格里每个币种各占一行；情景没有给出其变动时由你设定并标注「假设」，并用一句话说明依据（风险资产下跌的情景里，数字货币的假设跌幅通常应大于股票${h.stablecoins.length ? `；稳定币 ${h.stablecoins.join('、')} 按 0% 处理，另注明脱锚风险` : ''}）。
- 「应对顺序」至少 1 条针对数字货币：它 7×24 交易，可能先于美股开盘反应。` : ''}
- 用指定语言撰写；代码、公司名保留英文。全文不超过 900 字（中文）或 600 词（英文）。
- 最后一行固定为：「以上为信息整理，不构成投资建议。」`;

const LANGUAGE_NAME: Record<Locale, string> = { 'zh-CN': '简体中文', en: 'English', 'fr-CA': 'Français (Canada)' };

function nyTimeLine(now: number, locale: Locale): string {
  const d = new Date(now);
  const date = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const clock = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  const weekday = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'zh-CN', { timeZone: 'America/New_York', weekday: 'long' }).format(d);
  return `${date} ${clock} ET（${date}，${weekday}）`;
}

/** Non-USD-cash positions a daily report owes a sub-section each; USD cash has no news of its own. */
function mustCover(assets: Asset[]): string[] {
  return Array.from(new Set(assets.filter((a) => a.assetClass !== 'cash' || a.currency.toUpperCase() !== 'USD').map((a) => (a.assetClass === 'cash' ? `${a.currency.toUpperCase()} 现金` : a.symbol.toUpperCase()))));
}

const cryptoLine = (input: PromptInput) => {
  const h = heldBy(input.assets);
  return h.crypto.length ? [`数字货币持仓：${h.crypto.join('、')}`] : [];
};

function dailyUser(kind: string, input: PromptInput): string {
  const cover = mustCover(input.assets);
  return [
    `当前纽约时间：${nyTimeLine(input.now, input.locale)}`,
    `输出语言：${LANGUAGE_NAME[input.locale]}`,
    `日报类型：${kind}`,
    `搜索次数上限：${input.maxSearches}`,
    ...(cover.length ? [`必须逐项覆盖（${cover.length} 项，顺序按持仓上下文）：${cover.join('、')}`] : []),
    ...cryptoLine(input),
    '',
    '持仓上下文（自动生成；金额按占比表述；价格为报价时刻的现价）：',
    input.context,
  ].join('\n');
}

function offlineUser(input: PromptInput, extra: string[] = []): string {
  return [`当前纽约时间：${nyTimeLine(input.now, input.locale)}`, `输出语言：${LANGUAGE_NAME[input.locale]}`, ...extra, ...cryptoLine(input), '', '持仓上下文（自动生成；金额按占比表述；价格为报价时刻的现价）：', input.context].join('\n');
}

/** Searches the pre-market plan needs: one per position, three macro, one crypto-market, one per non-USD currency, two for calendar/futures. */
function premarketSearches(assets: Asset[]): number {
  const priced = assets.filter((a) => a.assetClass !== 'cash' || a.currency.toUpperCase() !== 'USD');
  const crypto = assets.some((a) => a.assetClass === 'crypto') ? 1 : 0;
  const fiat = new Set(assets.filter((a) => a.assetClass === 'cash' && a.currency.toUpperCase() !== 'USD').map((a) => a.currency.toUpperCase())).size;
  return Math.min(MAX_SEARCHES, priced.length + 3 + crypto + fiat + 2);
}

export const TEMPLATES: ReportTemplate[] = [
  {
    id: 'premarket',
    needsWeb: true,
    daily: true,
    available: () => true,
    estimateSearches: premarketSearches,
    system: (input) => `${GENERAL_RULES}\n\n${premarket(heldBy(input.assets))}`,
    user: (input) => dailyUser('前瞻日报', input),
  },
  {
    id: 'postmarket',
    needsWeb: true,
    daily: true,
    available: (now) => usSessionPhase(now) === 'post',
    estimateSearches: (assets) => Math.min(MAX_SEARCHES, assets.filter((a) => a.assetClass !== 'cash' || a.currency.toUpperCase() !== 'USD').length + 4 + (assets.some((a) => a.assetClass === 'crypto') ? 1 : 0) + (assets.some((a) => a.market === 'CA' && a.assetClass !== 'cash') ? 1 : 0)),
    system: (input) => `${GENERAL_RULES}\n\n${postmarket(heldBy(input.assets))}`,
    user: (input) => dailyUser('收盘日报', input),
  },
  {
    id: 'checkup',
    needsWeb: false,
    daily: false,
    available: () => true,
    estimateSearches: () => 0,
    system: (input) => checkup(heldBy(input.assets)),
    user: (input) => offlineUser(input),
  },
  {
    id: 'stress',
    needsWeb: false,
    daily: false,
    available: () => true,
    estimateSearches: () => 0,
    system: (input) => stress(heldBy(input.assets)),
    user: (input) => offlineUser(input, [`情景：${input.scenario?.trim() || t('tpl.stress.defaultScenario')}`]),
  },
];

export function templateById(id: ReportTemplateId): ReportTemplate {
  const tpl = TEMPLATES.find((x) => x.id === id);
  if (!tpl) throw new Error(`unknown template ${id}`);
  return tpl;
}

export function templateName(id: ReportTemplateId): string {
  return t(`tpl.${id}.name` as const);
}
